import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import EnhancedCameraCapture from '../../components/EnhancedCameraCapture';
import DroneFlightPanel from '../../components/DroneFlightPanel';
import Sphere360Viewer from '../../components/Sphere360Viewer';
import ConstructionContextSelector from '../../components/ConstructionContextSelector';
import LogQuantityEvidence from '../../components/LogQuantityEvidence';
import LinkActivityModal from '../../components/LinkActivityModal';
import CreateIssueModal, { deviationPrefill, sourceImageAttachment } from '../../components/CreateIssueModal';
import CostLinkModal from '../../components/CostLinkModal';
import { useOffline } from '../../contexts/OfflineContext';
import { useTranslation } from 'react-i18next';
import { 
  Upload, Camera, Play, Loader2, FileText, Download, 
  CheckCircle2, XCircle, AlertTriangle, Package, BarChart3, Image as ImageIcon, MapPin, X, AlertCircle, ClipboardCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadFileUrl } from '../../lib/download';
import { pollJobsToTerminal, resolveIngestionState, isAiUnavailableError } from '../../lib/jobPolling';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ObjectIntelligence = () => {
  const { projectId } = useParams();
  const { t } = useTranslation();
  const { queueCapture } = useOffline();
  const [project, setProject] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [constructionCtx, setConstructionCtx] = useState({});
  const [showLogEvidence, setShowLogEvidence] = useState(false);
  const [showLinkActivity, setShowLinkActivity] = useState(false);
  const [mode, setMode] = useState('count_similar');
  const [expectedQty, setExpectedQty] = useState('');
  const [running, setRunning] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [showEnhancedCamera, setShowEnhancedCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [captureSource, setCaptureSource] = useState(null);
  const [captureMeta, setCaptureMeta] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [allJobs, setAllJobs] = useState([]);
  const [batchProgress, setBatchProgress] = useState(null); // {done,total,phase,completed,failed}
  const [batchStatus, setBatchStatus] = useState(null); // {state,submitted,completed,failed,failedUploadIds,batchId}
  const [image360, setImage360] = useState(null); // dataUrl for pan viewer
  const [image360Job, setImage360Job] = useState(null); // completed 360 job (detections)
  const [image360Progress, setImage360Progress] = useState(null); // {stage,current,total} while processing
  const [image360Dims, setImage360Dims] = useState(null); // {w,h} natural size
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [batchReportBusy, setBatchReportBusy] = useState(null); // batchId being generated
  const [showCreateIssue, setShowCreateIssue] = useState(false);
  const [issueCtx, setIssueCtx] = useState(null);
  const [showCostLink, setShowCostLink] = useState(false);
  const [deviations, setDeviations] = useState([]);
  const batchInputRef = useRef(null);
  const input360Ref = useRef(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchProject();
    fetchUploads();
    fetchAllJobs();
  }, [projectId]);

  useEffect(() => {
    let pollInterval;
    if (currentJob && (currentJob.status === 'pending' || currentJob.status === 'processing')) {
      pollInterval = setInterval(() => {
        pollJobStatus(currentJob.id);
      }, 2000);
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [currentJob]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error(t('oi.toastFetchProjectFail'));
    }
  };

  const fetchUploads = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/uploads`);
      setUploads(response.data.filter(u => u.dataType === 'object_count' || u.mimeType.startsWith('image/')));
    } catch (error) {
      console.error('Failed to fetch uploads');
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraStream(stream);
      setShowCamera(true);
    } catch (error) {
      toast.error(t('oi.toastCameraFail'));
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg');
      setCapturedImage(imageData);
      setCaptureSource('camera_single');
      setCaptureMeta(null);
      stopCamera();
    }
  };

  const fetchAllJobs = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/analysis-jobs`);
      setAllJobs(res.data || []);
    } catch (e) { /* non-fatal */ }
    try {
      const dev = await axios.get(`${API}/projects/${projectId}/deviations`);
      setDeviations(dev.data?.deviations || []);
    } catch (e) { /* non-fatal */ }
  };

  const uploadImageFile = async (file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('dataType', 'object_count');
    const res = await axios.post(`${API}/projects/${projectId}/uploads`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.id;
  };

  const runBatchIngestion = async (files, batchId, existingUploadIds) => {
    // Phase 1 — upload (unless retrying already-persisted evidence), submit run jobs.
    const jobIds = [];
    let submitted = 0;
    const total = files ? files.length : (existingUploadIds || []).length;
    setBatchProgress({ done: 0, total, phase: 'uploading', completed: 0, failed: 0 });
    for (let i = 0; i < total; i++) {
      try {
        const uploadId = files ? await uploadImageFile(files[i]) : existingUploadIds[i];
        const res = await axios.post(`${API}/projects/${projectId}/object-intel/run`, {
          mode, expectedQty: expectedQty ? parseInt(expectedQty) : null,
          uploadId, batchId, captureSource: 'batch_upload',
          constructionContext: Object.keys(constructionCtx || {}).length ? constructionCtx : null,
        });
        if (res.data?.id) { jobIds.push(res.data.id); submitted += 1; }
      } catch (err) { /* upload/submit failure counts as failed below */ }
      setBatchProgress((p) => ({ ...(p || {}), done: i + 1, total, phase: 'uploading' }));
    }

    // Phase 2 — wait for AUTHORITATIVE terminal results from the backend.
    setBatchProgress({ done: 0, total: submitted, phase: 'processing', completed: 0, failed: 0 });
    const { completed, failed, aiUnavailable, failedUploadIds } = await pollJobsToTerminal(API, jobIds, {
      onTick: ({ completed: c, failed: f }) =>
        setBatchProgress({ done: c + f, total: submitted, phase: 'processing', completed: c, failed: f }),
    });
    const uploadFailures = total - submitted;
    const totalFailed = failed + uploadFailures;
    const state = resolveIngestionState({ submitted: total, completed, failed: totalFailed, aiUnavailable });

    setBatchProgress(null);
    setBatchStatus({ state, submitted: total, completed, failed: totalFailed, failedUploadIds, batchId });

    if (state === 'AI_UNAVAILABLE') {
      toast.error(t('oi.aiUnavailable', { defaultValue: 'AI analysis is currently unavailable because an AI provider has not been configured.' }));
    } else if (state === 'FAILED') {
      toast.error(t('oi.ingestFailed', { defaultValue: `Ingestion failed — 0 of ${total} images were analyzed.` }));
    } else if (state === 'PARTIALLY_COMPLETED') {
      toast.warning(t('oi.ingestPartial', { defaultValue: `Partially completed — ${completed} analyzed, ${totalFailed} failed.` }));
    } else {
      toast.success(t('oi.ingestComplete', { defaultValue: `Analysis complete — ${completed} of ${total} images processed.` }));
    }
    fetchAllJobs();
  };

  const handleBatchUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (batchInputRef.current) batchInputRef.current.value = '';
    if (files.length === 0) return;
    setBatchStatus(null);
    const batchId = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `batch-${Date.now()}`;
    await runBatchIngestion(files, batchId, null);
  };

  // Retry only the FAILED items — reuse already-persisted uploads (no duplicate uploads).
  const retryFailedBatch = async () => {
    if (!batchStatus || !batchStatus.failedUploadIds?.length) return;
    const ids = batchStatus.failedUploadIds;
    const batchId = batchStatus.batchId;
    setBatchStatus(null);
    await runBatchIngestion(null, batchId, ids);
  };

  const handle360Upload = async (e) => {
    const file = (e.target.files || [])[0];
    if (input360Ref.current) input360Ref.current.value = '';
    if (!file) return;
    setImage360Job(null);
    setImage360Progress(null);
    setSelectedHotspot(null);
    const reader = new FileReader();
    reader.onload = (ev) => setImage360(ev.target.result);
    reader.readAsDataURL(file);
    setRunning(true);
    try {
      const uploadId = await uploadImageFile(file);
      const runRes = await axios.post(`${API}/projects/${projectId}/object-intel/run`, {
        mode, expectedQty: expectedQty ? parseInt(expectedQty) : null,
        uploadId, is360: true, captureSource: '360',
        constructionContext: Object.keys(constructionCtx || {}).length ? constructionCtx : null,
      });
      toast.success(t('oi.toast360Uploaded'));
      // Poll the 360 job so we can overlay detected-object hotspots
      const jobId = runRes.data.id;
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const r = await axios.get(`${API}/analysis-jobs/${jobId}`);
          if (r.data.status === 'completed') {
            clearInterval(poll);
            setImage360Progress(null);
            setImage360Job(r.data);
            const n = (r.data.result?.detections || []).length;
            toast.success(n > 0 ? t('oi.toastObjectsDetected', { n }) : t('oi.toast360Complete'));
          } else if (r.data.status === 'failed' || tries > 40) {
            clearInterval(poll);
            setImage360Progress(null);
            setImage360Job(r.data);
            if (isAiUnavailableError(r.data)) {
              toast.error(t('oi.aiUnavailable', { defaultValue: 'AI analysis is currently unavailable because an AI provider has not been configured.' }));
            } else if (r.data.status === 'failed') {
              toast.error(t('oi.analysis360Failed', { defaultValue: '360° analysis failed. Please try again.' }));
            }
          } else {
            setImage360Progress(r.data.progress || null);
          }
        } catch (err) { clearInterval(poll); }
      }, 2000);
      fetchAllJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('oi.analysisFailedTitle'));
    } finally {
      setRunning(false);
    }
  };

  const openDeviationIssue = async (d) => {
    try {
      const jobRes = await axios.get(`${API}/analysis-jobs/${d.jobId}`);
      const jobFull = jobRes.data;
      const pre = deviationPrefill(jobFull);
      const uploadId = jobFull.inputUploadId || jobFull.uploadId;
      const photo = await sourceImageAttachment(projectId, uploadId);
      setIssueCtx({
        job: jobFull,
        titleOverride: pre.title,
        descriptionOverride: pre.description,
        initialAttachments: photo ? [photo] : [],
        initialAssignee: jobFull.createdBy || '',
      });
    } catch (e) {
      toast.error(t('oi.toastDeviationOpenFail'));
    }
  };

  const downloadBatchReport = async (batchId) => {
    setBatchReportBusy(batchId);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/object-intel/batch-report`,
        { batchId },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch_report_${batchId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('oi.toastBatchReport'));
    } catch (e) {
      toast.error(t('oi.toastBatchReportFail'));
    } finally {
      setBatchReportBusy(null);
    }
  };


  const runAnalysis = async () => {
    if (!selectedUpload && !capturedImage) {
      toast.error(t('oi.toastSelectImage'));
      return;
    }

    setRunning(true);
    try {
      const payload = {
        mode,
        expectedQty: expectedQty ? parseInt(expectedQty) : null,
        uploadId: selectedUpload?.id || null,
        imageData: capturedImage || null,
        captureSource: capturedImage ? (captureSource || 'camera_single') : null,
        captureMeta: capturedImage ? captureMeta : null,
        constructionContext: Object.keys(constructionCtx || {}).length ? constructionCtx : null,
      };

      // Phase 9: offline capture — queue to IndexedDB and auto-sync on reconnect.
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        await queueCapture({
          type: 'object-intel',
          projectId,
          projectName: project?.name,
          label: `${mode} capture`,
          payload,
        });
        toast.success(t('oi.toastSavedOffline'));
        setCapturedImage(null);
        setCaptureSource(null);
        setCaptureMeta(null);
        setSelectedUpload(null);
        setRunning(false);
        return;
      }

      const response = await axios.post(`${API}/projects/${projectId}/object-intel/run`, payload);
      setCurrentJob(response.data);
      toast.success(t('oi.toastStarted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('oi.toastFailedStart'));
      setRunning(false);
    }
  };

  const pollJobStatus = async (jobId) => {
    try {
      const response = await axios.get(`${API}/analysis-jobs/${jobId}`);
      setCurrentJob(response.data);
      
      if (response.data.status === 'completed') {
        setRunning(false);
        toast.success(t('oi.toastCompleted'));
      } else if (response.data.status === 'failed') {
        setRunning(false);
        toast.error(t('oi.toastFailed') + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to poll job status');
    }
  };

  const generateReport = async () => {
    if (!currentJob || currentJob.status !== 'completed') return;

    setGeneratingReport(true);
    try {
      const response = await axios.post(`${API}/analysis-jobs/${currentJob.id}/report`);
      await downloadFileUrl(response.data.reportUrl, `object_report_${currentJob.id.substring(0, 8)}.pdf`);
      toast.success(t('oi.toastReportGenerated'));
    } catch (error) {
      toast.error(t('oi.toastReportFail'));
    } finally {
      setGeneratingReport(false);
    }
  };

  const result = currentJob?.result;
  const summary = result?.summary;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('oi.title')}</h1>
          <p className="text-slate-600 mt-1">{project?.name}</p>
        </div>

        {/* Phase 1: Construction context — link this capture to a physical location */}
        <ConstructionContextSelector projectId={projectId} value={constructionCtx} onChange={setConstructionCtx} />

        {/* Input Selection */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('oi.step1')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <button onClick={() => { setShowCamera(false); setShowEnhancedCamera(false); }} className={`p-4 border-2 rounded-lg transition-all ${!showCamera && !showEnhancedCamera && !capturedImage ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`} data-testid="select-upload-btn">
              <Upload className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p className="text-sm font-medium">{t('oi.selectUpload')}</p>
            </button>
            
            <button onClick={startCamera} className={`p-4 border-2 rounded-lg transition-all ${showCamera ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`} data-testid="camera-capture-btn">
              <Camera className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p className="text-sm font-medium">{t('oi.quickCamera')}</p>
              <p className="text-xs text-slate-500 mt-1">{t('oi.basicCapture')}</p>
            </button>
            
            <button onClick={() => { stopCamera(); setShowCamera(false); setShowEnhancedCamera(true); }} className={`p-4 border-2 rounded-lg transition-all ${showEnhancedCamera ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`} data-testid="enhanced-camera-btn">
              <Camera className="w-6 h-6 mx-auto mb-2 text-green-600" />
              <p className="text-sm font-medium">{t('oi.smartCamera')}</p>
              <p className="text-xs text-slate-500 mt-1">{t('oi.qualityBurst')}</p>
            </button>
            
            <label className="p-4 border-2 border-slate-200 rounded-lg hover:border-blue-300 transition-all cursor-pointer">
              <ImageIcon className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p className="text-sm font-medium">{t('oi.uploadNew')}</p>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setCapturedImage(event.target.result);
                    setCaptureSource(null);
                    setCaptureMeta(null);
                  };
                  reader.readAsDataURL(file);
                }
              }} />
            </label>
          </div>

          {showCamera && (
            <div className="space-y-4">
              <video ref={videoRef} autoPlay playsInline className="w-full max-w-md mx-auto rounded-lg border-2 border-slate-300" />
              <div className="flex justify-center gap-3">
                <button onClick={capturePhoto} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{t('oi.capture')}</button>
                <button onClick={stopCamera} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300">{t('common.cancel')}</button>
              </div>
              <canvas ref={canvasRef} className="hidden" />
            </div>
          )}

          {!showCamera && !capturedImage && (
            <select value={selectedUpload?.id || ''} onChange={(e) => setSelectedUpload(uploads.find(u => u.id === e.target.value))} className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg" data-testid="upload-select">
              <option value="">{t('oi.selectUploadedImage')}</option>
              {uploads.map((upload) => (
                <option key={upload.id} value={upload.id}>{upload.fileName}</option>
              ))}
            </select>
          )}

          {capturedImage && !showCamera && (
            <div className="relative">
              <img src={capturedImage} alt="Captured" className="w-full max-w-md mx-auto rounded-lg border-2 border-slate-300" />
              <button onClick={() => { setCapturedImage(null); setCaptureSource(null); setCaptureMeta(null); }} className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Mode Selection */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('oi.step2')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <button onClick={() => setMode('count_similar')} className={`p-4 border-2 rounded-lg transition-all ${mode === 'count_similar' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`} data-testid="mode-count">
              <Package className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p className="text-sm font-medium">{t('oi.modeCount')}</p>
            </button>
            
            <button onClick={() => setMode('quality_inspection')} className={`p-4 border-2 rounded-lg transition-all ${mode === 'quality_inspection' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`} data-testid="mode-quality">
              <BarChart3 className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p className="text-sm font-medium">{t('oi.modeQuality')}</p>
            </button>
            
            <button onClick={() => setMode('combined')} className={`p-4 border-2 rounded-lg transition-all ${mode === 'combined' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`} data-testid="mode-combined">
              <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p className="text-sm font-medium">{t('oi.modeCombined')}</p>
            </button>
          </div>

          {(mode === 'count_similar' || mode === 'combined') && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('oi.expectedQtyLabel')}</label>
              <input type="number" value={expectedQty} onChange={(e) => setExpectedQty(e.target.value)} placeholder={t('oi.expectedQtyPlaceholder')} className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg" data-testid="expected-qty-input" />
            </div>
          )}
        </div>

        {/* Run Analysis */}
        <button onClick={runAnalysis} disabled={running || (!selectedUpload && !capturedImage)} className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md" data-testid="run-analysis-btn">
          {running ? <><Loader2 className="w-5 h-5 animate-spin" /><span>{t('oi.analyzing')}</span></> : <><Play className="w-5 h-5" /><span>{t('oi.runAnalysis')}</span></>}
        </button>

        {/* Multi-Photo & 360 ingestion */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="batch-360-panel">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">{t('oi.batch360Title')}</h2>
          <p className="text-sm text-slate-600 mb-4">{t('oi.batch360Desc', { mode })}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <input ref={batchInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleBatchUpload} data-testid="batch-file-input" />
              <button onClick={() => batchInputRef.current?.click()} disabled={!!batchProgress} className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-purple-300 rounded-lg py-6 hover:bg-purple-50 disabled:opacity-60" data-testid="batch-upload-btn">
                <Upload className="w-6 h-6 text-purple-600" />
                <span className="font-medium">{batchProgress ? (batchProgress.phase === 'processing' ? `Processing ${batchProgress.done} of ${batchProgress.total}… (${batchProgress.completed} ok, ${batchProgress.failed} failed)` : t('oi.analyzingProgress', { done: batchProgress.done, total: batchProgress.total })) : t('oi.uploadMultiple')}</span>
              </button>
            </div>
            <div>
              <input ref={input360Ref} type="file" accept="image/jpeg,image/jpg" className="hidden" onChange={handle360Upload} data-testid="input-360" />
              <button onClick={() => input360Ref.current?.click()} disabled={running} className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-teal-300 rounded-lg py-6 hover:bg-teal-50 disabled:opacity-60" data-testid="upload-360-btn">
                <Camera className="w-6 h-6 text-teal-600" />
                <span className="font-medium">{t('oi.photo360')}</span>
              </button>
            </div>
          </div>

          {/* Truthful ingestion status — reflects AUTHORITATIVE backend job results */}
          {batchStatus && (
            <div
              className={`mt-4 rounded-lg p-4 border text-sm ${
                batchStatus.state === 'COMPLETED' ? 'bg-green-50 border-green-200 text-green-800' :
                batchStatus.state === 'PARTIALLY_COMPLETED' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                batchStatus.state === 'AI_UNAVAILABLE' ? 'bg-slate-50 border-slate-300 text-slate-700' :
                'bg-red-50 border-red-200 text-red-800'
              }`}
              data-testid="batch-status-banner"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold" data-testid="batch-status-state">{batchStatus.state.replace(/_/g, ' ')}</p>
                  {batchStatus.state === 'AI_UNAVAILABLE' ? (
                    <p className="mt-1">AI analysis is currently unavailable because an AI provider has not been configured. Your images were uploaded and are preserved.</p>
                  ) : (
                    <p className="mt-1" data-testid="batch-status-counts">Completed: {batchStatus.completed} · Failed: {batchStatus.failed} · Submitted: {batchStatus.submitted}</p>
                  )}
                </div>
                {batchStatus.failed > 0 && batchStatus.failedUploadIds?.length > 0 && batchStatus.state !== 'AI_UNAVAILABLE' && (
                  <button onClick={retryFailedBatch} disabled={!!batchProgress} className="shrink-0 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs hover:bg-slate-900 disabled:opacity-50" data-testid="batch-retry-failed-btn">
                    Retry Failed ({batchStatus.failedUploadIds.length})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* 360 immersive sphere viewer with detected-object hotspots */}
          {image360 && (
            <div className="mt-4">
              <p className="text-xs text-slate-500 mb-1">
                {t('oi.panViewerHint')}{(image360Job?.result?.detections || []).length > 0 ? t('oi.panViewerHintTap') : ''}
              </p>
              {image360Progress && image360Progress.total ? (
                <div className="mb-2 flex items-center gap-2 text-xs text-teal-700" data-testid="sphere-360-progress">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {t('oi.analyzingSegment', { defaultValue: 'Analyzing view segment {{current}}/{{total}}…', current: image360Progress.current, total: image360Progress.total })}
                </div>
              ) : null}
              <Sphere360Viewer
                imageUrl={image360}
                detections={image360Job?.result?.detections || []}
              />
              {image360Job && (image360Job.result?.detections || []).length === 0 && image360Job.status === 'completed' && (
                <p className="text-xs text-slate-400 mt-1" data-testid="no-hotspots">{t('oi.noHotspots')}</p>
              )}
            </div>
          )}
        </div>

        {/* Drone Flight Ingestion */}
        <DroneFlightPanel
          projectId={projectId}
          mode={mode}
          expectedQty={expectedQty}
          jobs={allJobs}
          onRefresh={fetchAllJobs}
        />

        {/* Flagged Deviations (Phase 4) */}
        {deviations.length > 0 && (
          <div className="bg-white rounded-xl p-6 border border-red-200 shadow-sm" data-testid="deviations-panel">
            <h2 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" /> {t('oi.flaggedDeviations')}
            </h2>
            <p className="text-sm text-slate-500 mb-3">{t('oi.flaggedDeviationsDesc')}</p>
            <div className="space-y-2">
              {deviations.map((d) => (
                <div key={d.jobId} className="flex items-center justify-between border border-slate-200 rounded-lg p-3" data-testid="deviation-row">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate">{d.roomName || d.type} <span className="text-xs text-slate-400 font-mono">{d.jobId.slice(0, 8)}</span></p>
                    <p className="text-xs text-slate-500">{t('oi.expected')} {d.deviation.expected} · {t('oi.actual')} {d.deviation.actual}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700">
                      {d.deviation.percent > 0 ? '+' : ''}{d.deviation.percent}%
                    </span>
                    <button onClick={() => openDeviationIssue(d)} className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs flex items-center gap-1 hover:bg-amber-700" data-testid="deviation-create-issue-btn">
                      <AlertCircle className="w-3 h-3" /> {t('oi.createIssue')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Batch results grouped by batchId */}
        {(() => {
          const batches = {};
          allJobs.forEach((j) => { if (j.batchId && j.captureSource !== 'drone') { (batches[j.batchId] = batches[j.batchId] || []).push(j); } });
          const entries = Object.entries(batches);
          if (entries.length === 0) return null;
          return (
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="batch-results">
              <h2 className="text-lg font-semibold text-slate-900 mb-3">Batch Results</h2>
              <div className="space-y-4">
                {entries.map(([bid, bjobs]) => {
                  const total = bjobs.reduce((s, j) => s + Number(j.result?.count ?? j.result?.totalCount ?? j.result?.summary?.totalCount ?? 0), 0);
                  return (
                    <div key={bid} className="border border-slate-200 rounded-lg p-3" data-testid="batch-group">
                      <div className="flex justify-between items-center mb-2">
                        <p className="font-medium text-slate-800">{t('oi.batchLabel', { id: bid.substring(0, 8), count: bjobs.length })}</p>
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold" data-testid="batch-total">{t('oi.total')} {total}</span>
                          <button
                            onClick={() => downloadBatchReport(bid)}
                            disabled={batchReportBusy === bid}
                            className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs flex items-center gap-1 hover:bg-purple-700 disabled:opacity-60"
                            data-testid="batch-report-btn"
                          >
                            {batchReportBusy === bid ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} {t('oi.report')}
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {bjobs.map((j) => (
                          <div key={j.id} className="text-xs bg-slate-50 rounded p-2 border border-slate-100">
                            <span className={`inline-block px-1.5 py-0.5 rounded ${j.status === 'completed' ? 'bg-green-100 text-green-700' : j.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{j.status}</span>
                            <span className="ml-1">{j.result?.count ?? j.result?.totalCount ?? '—'} {t('oi.objs')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}


        {/* Results */}
        {currentJob && (
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">{t('oi.analysisResults')}</h2>
              <div className="flex items-center gap-2">
                {currentJob.status === 'completed' && summary && (
                  <button onClick={() => setShowLogEvidence(true)} className="px-3 py-1 text-sm font-medium rounded-full bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1" data-testid="log-evidence-btn">
                    <ClipboardCheck className="w-4 h-4" /> Log as BOQ Evidence
                  </button>
                )}
                {currentJob.status === 'completed' && summary && (
                  <button onClick={() => setShowLinkActivity(true)} className="px-3 py-1 text-sm font-medium rounded-full bg-sky-700 text-white hover:bg-sky-800 flex items-center gap-1" data-testid="link-activity-btn">
                    <ClipboardCheck className="w-4 h-4" /> Link to Activity
                  </button>
                )}
                {result?.deviation?.flagged && (
                  <span className="px-3 py-1 text-sm font-semibold rounded-full bg-red-100 text-red-700 flex items-center gap-1" data-testid="deviation-badge">
                    <AlertTriangle className="w-4 h-4" /> {t('oi.deviation')} {result.deviation.percent > 0 ? '+' : ''}{result.deviation.percent}%
                  </span>
                )}
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${currentJob.status === 'completed' ? 'bg-green-100 text-green-700' : currentJob.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`} data-testid="job-status">
                  {currentJob.status}
                </span>
              </div>
            </div>

            {currentJob.status === 'completed' && summary && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200" data-testid="summary-card-type">
                    <p className="text-sm text-slate-600 mb-1">{t('oi.objectType')}</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.objectType}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200" data-testid="summary-card-count">
                    <p className="text-sm text-slate-600 mb-1">{t('oi.totalCount')}</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.totalCount}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200" data-testid="summary-card-confidence">
                    <p className="text-sm text-slate-600 mb-1">{t('oi.confidence')}</p>
                    <p className="text-2xl font-bold text-slate-900">{(summary.confidence * 100).toFixed(0)}%</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200" data-testid="summary-card-quality">
                    <p className="text-sm text-slate-600 mb-1">{t('oi.qualityScore')}</p>
                    <p className="text-2xl font-bold text-slate-900">{(summary.qualityScore * 100).toFixed(0)}%</p>
                  </div>
                </div>

                {/* Issues */}
                {summary.issues && summary.issues.some(i => i.percent > 0) && (
                  <div>
                    <h3 className="text-md font-semibold text-slate-900 mb-3">{t('oi.qualityIssues')}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {summary.issues.filter(i => i.percent > 0).map((issue, idx) => (
                        <div key={idx} className="bg-red-50 p-3 rounded-lg border border-red-200">
                          <p className="text-sm font-medium text-slate-700">{issue.type.replace('_', ' ')}</p>
                          <p className="text-xl font-bold text-red-600">{issue.percent}%</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Clusters */}
                {result.clusters && result.clusters.length > 0 && (
                  <div>
                    <h3 className="text-md font-semibold text-slate-900 mb-3">{t('oi.objectClusters')}</h3>
                    <div className="space-y-2">
                      {result.clusters.map((cluster, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <div>
                            <p className="font-medium text-slate-900">Cluster {cluster.name}</p>
                            <p className="text-sm text-slate-600">{cluster.description}</p>
                          </div>
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">{cluster.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reconciliation */}
                {result.reconciliation && result.reconciliation.expectedQty && (
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h3 className="text-md font-semibold text-slate-900 mb-3">{t('oi.countReconciliation')}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-slate-600">{t('oi.expected')}</p>
                        <p className="text-xl font-bold text-slate-900">{result.reconciliation.expectedQty}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">{t('oi.detected')}</p>
                        <p className="text-xl font-bold text-slate-900">{summary.totalCount}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">{t('oi.variance')}</p>
                        <p className="text-xl font-bold text-slate-900">{result.reconciliation.variance}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">{t('oi.variancePct')}</p>
                        <p className="text-xl font-bold text-slate-900">{result.reconciliation.variancePercent?.toFixed(1)}%</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {result.recommendations && result.recommendations.length > 0 && (
                  <div>
                    <h3 className="text-md font-semibold text-slate-900 mb-3">{t('oi.recommendations')}</h3>
                    <ul className="space-y-2">
                      {result.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Generate Report */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={generateReport} disabled={generatingReport} className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 shadow-md" data-testid="generate-report-btn">
                    {generatingReport ? <><Loader2 className="w-5 h-5 animate-spin" /><span>{t('oi.generating')}</span></> : <><FileText className="w-5 h-5" /><span>{t('oi.generatePdf')}</span></>}
                  </button>
                  <button onClick={() => setShowCreateIssue(true)} className="flex-1 flex items-center justify-center gap-2 bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700 shadow-md" data-testid="create-issue-btn">
                    <AlertCircle className="w-5 h-5" /><span>{t('oi.createIssue')}</span>
                  </button>
                  <button onClick={() => setShowCostLink(true)} className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 text-white py-3 rounded-lg hover:bg-emerald-700 shadow-md" data-testid="link-cost-code-btn">
                    <FileText className="w-5 h-5" /><span>{t('oi.linkCostCode')}</span>
                  </button>
                </div>
                {currentJob.costLinks && currentJob.costLinks.length > 0 && (
                  <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="cost-links-summary">
                    <p className="text-sm font-semibold text-emerald-800 mb-1">{t('oi.costSummary')}</p>
                    {currentJob.costLinks.map((l, i) => (
                      <div key={i} className="flex justify-between text-sm text-emerald-700"><span>{l.code} · {l.quantity} × ₹{l.unitCost}</span><span className="font-semibold">₹{l.total}</span></div>
                    ))}
                    <div className="flex justify-between text-sm font-bold text-emerald-900 border-t border-emerald-200 mt-1 pt-1"><span>{t('oi.total')}</span><span>₹{currentJob.costLinks.reduce((s, l) => s + l.total, 0).toFixed(2)}</span></div>
                  </div>
                )}
              </div>
            )}

            {currentJob.status === 'failed' && (
              <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <div>
                  <p className="font-medium text-red-900">{t('oi.analysisFailedTitle')}</p>
                  <p className="text-sm text-red-700">{currentJob.error}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Enhanced Camera Component */}
      {showEnhancedCamera && (
        <EnhancedCameraCapture
          onCapture={(imageDataUrl, meta) => {
            setCapturedImage(imageDataUrl);
            setCaptureSource(meta?.captureSource || 'camera_single');
            setCaptureMeta(meta?.captureMeta || null);
            setShowEnhancedCamera(false);
            toast.success('Photo captured successfully!');
          }}
          onCancel={() => {
            setShowEnhancedCamera(false);
          }}
        />
      )}

      <CreateIssueModal
        projectId={projectId}
        job={currentJob}
        open={showCreateIssue}
        onClose={() => setShowCreateIssue(false)}
        onCreated={() => toast.success('Issue added to the project punch list')}
      />

      {/* Auto punch-item from a flagged deviation */}
      <CreateIssueModal
        projectId={projectId}
        job={issueCtx?.job}
        titleOverride={issueCtx?.titleOverride}
        descriptionOverride={issueCtx?.descriptionOverride}
        initialAttachments={issueCtx?.initialAttachments}
        initialAssignee={issueCtx?.initialAssignee}
        open={!!issueCtx}
        onClose={() => setIssueCtx(null)}
        onCreated={() => { toast.success('Punch-list issue created from deviation'); fetchAllJobs(); }}
      />

      <CostLinkModal
        projectId={projectId}
        job={currentJob}
        open={showCostLink}
        onClose={() => setShowCostLink(false)}
        onLinked={(data) => { setCurrentJob((prev) => prev ? { ...prev, costLinks: data.costLinks } : prev); fetchAllJobs(); }}
      />
      {showLogEvidence && currentJob?.result?.summary && (
        <LogQuantityEvidence
          projectId={projectId}
          aiQuantity={currentJob.result.summary.totalCount}
          sourceType="IMAGE"
          sourceId={currentJob.id}
          unit="NOS"
          constructionContext={constructionCtx}
          onClose={() => setShowLogEvidence(false)}
          onLogged={() => {}}
        />
      )}
      {showLinkActivity && currentJob?.id && (
        <LinkActivityModal
          projectId={projectId}
          evidenceType="job"
          evidenceId={currentJob.id}
          constructionContext={constructionCtx}
          onClose={() => setShowLinkActivity(false)}
          onLinked={() => {}}
        />
      )}
    </Layout>
  );
};

export default ObjectIntelligence;
